import { quarterMonths, toCents, type Priority, type SettlementStatus } from '@progear/finance-core';
import { z } from 'zod';

/**
 * Cash-flow client for module 1.1 (the primary view). Talks to the single
 * `/api/cash-flow` surface, Zod-validates the payload at the boundary (anti-slop
 * rule: every external boundary is validated), and exposes the typed shapes the
 * treasury table + planner render. All money is integer cents (L9) — this layer
 * never computes amounts, it only SELECTS a field for display (NET/GROSS).
 */

/** Money is integer cents (L9) — the boundary rejects any float/unsafe value. */
const cents = z.number().int().safe();
const moneySum = z.object({ net: cents, vat: cents, gross: cents });
const certaintyBuckets = z.object({
  actual: moneySum,
  committed: moneySum,
  forecast: moneySum,
  total: moneySum,
});

export const dayRollupSchema = z.object({
  date: z.string(),
  income: certaintyBuckets,
  cost: certaintyBuckets,
  net: moneySum,
  carriedForwardIncome: moneySum,
  carriedForwardCost: moneySum,
  runningBalance: cents,
  /** Confirmed-only twin of runningBalance (forecast money excluded) — the
   *  "kinnitatud" balance sub-line both lenses show (finance-core rollupDays). */
  confirmedRunningBalance: cents,
});

const entryMoney = z.object({
  net: cents,
  vat: cents,
  gross: cents,
  vatRate: z.number().int().min(0),
  vatMode: z.enum(['domestic', 'foreign', 'reduced', 'exempt']),
});

/** The settlement-source vocabulary (mirrors finance-core `CashFlowSource`). */
const settlementSource = z.enum(['manual', 'smartaccounts', 'erply', 'pipedrive', 'loan', 'tax', 'lhv', 'forecast', 'payment_plan', 'contract']);

/**
 * One source's settlement observation (per-source breakdown from the
 * cash_flow_settlement_by_source view). Structurally identical to finance-core's
 * `SourceSettlement`, so an entry's `sources` feeds the source-priority resolution
 * (`effectivePaidAmountOf` / `settlementSourcesOf`) directly. Competing observations
 * of the SAME euro — never summed across sources (no double-count, L9).
 */
export const sourceSettlementSchema = z.object({
  source: settlementSource,
  paidCents: cents,
  latestPaidOn: z.string().nullable(),
  observedAt: z.string().nullable(),
  paymentCount: z.number().int().min(0),
});
export type SourceSettlement = z.infer<typeof sourceSettlementSchema>;

export const kmdPaymentEventSchema = z.object({
  id: z.string(),
  paidOn: z.string(),
  amountCents: cents,
  source: z.enum(['manual', 'lhv']),
  supersededByPaymentId: z.string().nullable(),
  bankLinkId: z.string().nullable(),
});

export const kmdSettlementSchema = z.object({
  month: z.string().regex(/^\d{4}-\d{2}$/),
  finalized: z.boolean(),
  payments: z.array(kmdPaymentEventSchema),
});

export type KmdPaymentEvent = z.infer<typeof kmdPaymentEventSchema>;
export type KmdSettlement = z.infer<typeof kmdSettlementSchema>;

/**
 * One related document of a cash-flow entry — today only the prepayment-offset link
 * (ETT ettemaksuarve ↔ ARV lõpparve). Mirrors the API's `ApiRelatedDocument` (the
 * cash_flow_entry_related_document view): the DB exposes the LINK FACTS, finance-core
 * RESOLVES the euro offset (one truth, L9). `direction`, `amountCents` and
 * `relatedSettledCents` satisfy finance-core's `RelatedCashFlowDocument`, so an entry's
 * `relatedDocuments` feeds totalDocumentOffsetOf / effectiveCashFlowMoneyOf directly
 * — this layer never nets amounts itself. The rest is display identity (which document,
 * its number, party, gross, dates) for the "Seotud dokumendid" panel across every lens.
 */
export const relatedDocumentSchema = z.object({
  /** The link row id (cash_flow_entry_link.id) — the unlink handle. */
  linkId: z.string(),
  /** Both net (0040): `prepayment_offset` (ETT↔ARV) and `credit_note` (kreedit↔arve). */
  relationKind: z.enum(['prepayment_offset', 'credit_note']),
  /** Whether the related doc FUNDS this one (applied_from) or is funded BY it. */
  direction: z.enum(['applied_from', 'applies_to']),
  /** The link capacity, integer cents (display only — finance-core gates by cash). */
  amountCents: cents,
  effectiveOn: z.string(),
  /** The id of the OTHER document in the link. */
  relatedEntryId: z.string(),
  relatedDocType: z
    .enum(['invoice', 'prepayment', 'credit_note', 'offer', 'delivery_invoice'])
    .nullable(),
  relatedDocNumber: z.string().nullable(),
  relatedParty: z.string(),
  relatedKind: z.enum(['income', 'cost']),
  relatedGrossCents: cents,
  relatedExpectedDate: z.string(),
  relatedActualDate: z.string().nullable(),
  /** How much of the RELATED doc is itself settled in cash — the offset GATE (L9). */
  relatedSettledCents: cents,
  /** Σ of the related doc's own prepayment-link capacities — credit-note triangle facts. */
  relatedPrepaymentLinkCents: cents,
  /** Σ of those linked prepayments' own settled cash — credit-note triangle facts. */
  relatedPrepaymentPaidCents: cents,
});
export type RelatedDocument = z.infer<typeof relatedDocumentSchema>;

export const cashFlowEntrySchema = z.object({
  id: z.string(),
  kind: z.enum(['income', 'cost']),
  party: z.string(),
  description: z.string().nullable(),
  category: z.enum([
    'sale',
    'direct_cost',
    'operating_cost',
    'payroll',
    'financial_cost',
    'tax_agreement',
    'loan',
  ]),
  certainty: z.enum(['actual', 'committed', 'forecast']),
  source: z.enum(['manual', 'smartaccounts', 'erply', 'pipedrive', 'loan', 'tax', 'lhv', 'forecast', 'payment_plan', 'contract']),
  money: entryMoney,
  expectedDate: z.string(),
  actualDate: z.string().nullable(),
  // Document (issue) date — when the document was created, distinct from the due
  // date (expectedDate). Null on statutory/loan rows (not documents).
  documentDate: z.string().nullable(),
  isOverdue: z.boolean(),
  // Installment linkage: when an entry is split, every part shares one groupId and
  // remembers the original due day (originExpectedDate). The part that still sits on
  // that origin day is the "ghost" remainder — see isGhostEntry below.
  groupId: z.string().nullable(),
  originExpectedDate: z.string().nullable(),
  // Document metadata (1.1 canvas parity) — null on statutory/loan rows. Codes are
  // English standard terms; the UI maps each to its Estonian label (replit.md).
  docType: z.enum(['invoice', 'prepayment', 'credit_note', 'offer', 'delivery_invoice', 'none']).nullable(),
  txnType: z.enum(['retail', 'ecommerce', 'project_sale', 'wholesale', 'service', 'rental']).nullable(),
  project: z.string().nullable(),
  author: z.string().nullable(),
  // Document identity (1.1 receivables parity) — null on statutory/loan rows.
  docNumber: z.string().nullable(),
  offerNumber: z.string().nullable(),
  projectName: z.string().nullable(),
  // Free-text user note ("Kommentaar") — distinct from description; editable.
  comment: z.string().nullable(),
  // Importance flag (KRIITILINE/OLULINE/FOOKUSES) — null on most rows; editable
  // from the register. Codes are English; the UI maps each to its Estonian label.
  priority: z.enum(['critical', 'important', 'focus']).nullable(),
  // System-materializer key (`payroll:<M>:net`, `loan:<id>:…`) — null on
  // human-created rows. Present on BOTH lenses + register + detail (0038), so
  // every surface recognizes system-settled rows the SAME way (L14). Defaults to
  // null so pre-0038 fixtures/acknowledgements still parse.
  externalId: z.string().nullable().default(null),
  // Settlement projection, read straight from the cash_flow_day view. `paidCents`
  // is the event-sourced sum of payments. NEVER trust the view's actualDate-blind
  // outstanding — derive it via finance-core (outstandingOf / paidPctOf /
  // settlementStatusOf), which reconciles legacy mark-paid with partial events.
  paidCents: cents,
  // Settled by adjustment (write-off / factoring), integer cents — kept separate
  // from cash `paidCents` so the register shows real cash vs a write-off. Counts
  // toward "settled" in finance-core (outstandingOf / settledPctOf / status).
  adjustedCents: cents,
  paymentCount: z.number().int().min(0),
  lastPaidOn: z.string().nullable(),
  // Per-source settlement breakdown (✓ + date + which source is the truth). Defaults
  // to [] so a mutation acknowledgement (which omits it) still parses. finance-core
  // resolves the source-priority TRUTH; never summed across sources (no double-count).
  sources: z.array(sourceSettlementSchema).default([]),
  // Related documents (prepayment ETT ↔ ARV and credit-note KRE ↔ ARV links).
  // Defaults to [] so a mutation acknowledgement (which omits it) still parses,
  // exactly like `sources`. Carries finance-core's RelatedCashFlowDocument shape,
  // so the entry nets via totalDocumentOffsetOf / effectiveCashFlowMoneyOf —
  // never summed here (L9).
  relatedDocuments: z.array(relatedDocumentSchema).default([]),
  reconciliation: z.object({
    side: z.enum(['forecast', 'actual']).nullable(),
    originalForecastCents: cents.nullable(),
    allocatedForecastCents: cents,
    linkedActualCents: cents,
    remainingForecastCents: cents,
    varianceCents: cents,
    isFullyReconciled: z.boolean(),
    links: z.array(z.object({
      linkId: z.string(), forecastEntryId: z.string(), actualEntryId: z.string(),
      forecastCents: cents, actualCents: cents,
      mode: z.enum(['replacement', 'partial']),
      matchSource: z.enum(['auto', 'manual']),
      matchReason: z.string().nullable(),
      createdAt: z.string(),
      counterpart: z.object({
        id: z.string(), party: z.string(), description: z.string().nullable(),
        expectedDate: z.string(), docNumber: z.string().nullable(),
        source: z.string(), grossCents: cents,
      }),
    })),
  }).nullable().optional(),
  // Event-level KMD settlement. `money` remains the immutable forecast baseline;
  // finance-core derives each actual installment and the forecast remainder.
  kmdSettlement: kmdSettlementSchema.optional(),
});

/** The OPENING anchor — the one-time "aasta alguse seis" the balance rolls forward from. */
const anchorSchema = z.object({ balanceCents: cents, asOfDate: z.string() }).nullable();
/** Newest observed bank balance (e.g. LHV import) — informational only, never the
 *  balance-line anchor. Optional so an older API response still parses. */
const latestBalanceSchema = z
  .object({ balanceCents: cents, asOfDate: z.string(), source: z.enum(['manual', 'lhv']) })
  .nullable()
  .optional();

export const cashFlowResponseSchema = z.object({
  from: z.string(),
  to: z.string(),
  today: z.string(),
  openingBalance: cents,
  anchor: anchorSchema,
  latestBalance: latestBalanceSchema,
  days: z.array(dayRollupSchema),
  entries: z.array(cashFlowEntrySchema),
});

const entryEnvelopeSchema = z.object({ entry: cashFlowEntrySchema });
const splitResponseSchema = z.object({ entries: z.array(cashFlowEntrySchema) });

export type MoneySum = z.infer<typeof moneySum>;
export type DayRollup = z.infer<typeof dayRollupSchema>;
export type CashFlowEntry = z.infer<typeof cashFlowEntrySchema>;
export type CashFlowResponse = z.infer<typeof cashFlowResponseSchema>;

/** Which monetary field the global toggle is showing — selection, never maths. */
export type MoneyMode = 'net' | 'gross';

/**
 * The shared forecast-emphasis class — the ONE place "rõhuta prognoos" turns into a
 * visual cue, consumed identically by every primary-view lens (calendar chip, register
 * row, matrix forecast column) so the toggle lifts forecast entries everywhere at once
 * (replit.md L14: an enhancement targets the shared layer, never a single lens). Returns
 * the amber `--pending` wash for a forecast unit while the toggle is on, else the empty
 * string. Pure display — it never touches a number (L9).
 */
export function forecastEmphasisClass(
  certainty: 'actual' | 'committed' | 'forecast',
  emphasis: boolean,
): string {
  return emphasis && certainty === 'forecast' ? 'bg-[hsl(var(--pending))]/10' : '';
}

/**
 * The shared forecast BASELINE class — a forecast (`prognoos`) entry reads a touch
 * distinct from a committed/actual one at ALL times, not only while "rõhuta prognoos"
 * is on, so a plan never looks like a confirmed figure. The ONE place the baseline wash
 * is defined, applied wherever the cue is a background wash (the register row + the matrix
 * forecast column). The calendar chip expresses the SAME forecast state with its own
 * lens-appropriate dashed border — it does NOT call this helper, because a background wash
 * there would clobber the chip's income/cost colour. The toggle's amber
 * `forecastEmphasisClass` layers ON TOP of this baseline. Pure display — it never touches
 * a number (L9); one definition for the wash, every lens distinct (L14).
 */
export function forecastBaseClass(certainty: 'actual' | 'committed' | 'forecast'): string {
  return certainty === 'forecast' ? 'bg-muted/20' : '';
}

/**
 * The shared forecast MARKER pill — label + title + class, the ONE definition of the
 * always-on "this is a plan, not a confirmed figure" badge. Rendered per-entry in BOTH
 * per-entry lenses (the calendar chip + the register row) so a forecast (`prognoos`)
 * reads unmistakably distinct at ALL times, not only while the "rõhuta prognoos" toggle
 * is on — the toggle's amber wash (`forecastEmphasisClass`) only LIFTS an already-marked
 * entry. One definition, identical in every lens (replit.md L14). The hollow ○ glyph
 * reads "unconfirmed" against the filled certainty of an actual/committed row. Pure
 * display; never touches a number (L9).
 */
export const FORECAST_GLYPH = '○';
export const FORECAST_PILL_LABEL = `${FORECAST_GLYPH} Prognoos`;
export const FORECAST_PILL_TITLE = 'Prognoos — planeeritud, veel kinnitamata summa';
export const FORECAST_PILL_CLS =
  'shrink-0 rounded border border-dashed border-[hsl(var(--pending))]/70 px-1 text-[9px] font-medium uppercase leading-none tracking-wide text-[hsl(var(--pending))]';

/**
 * The shared "kontrolli" MARKER on a stale katteta ettemaks — glyph + title + class,
 * the ONE definition of the review badge finance-core's `prepaymentReviewNeeded`
 * drives: an income-side ETT past its due day with no cash received and no final
 * invoice / kreeditarve linked. Rendered per-entry in BOTH per-entry lenses (the
 * calendar chip + the register Staatus cell); the matrix reaches it through the same
 * scoped drill-to-register every aggregate uses (L14). Amber (`--pending`) matches
 * the ETT document badge so the cue reads as "this prepayment needs attention".
 * Pure display; the decision itself is finance-core's (master rule: one truth).
 */
export const PREPAYMENT_REVIEW_GLYPH = '⚠';
export const PREPAYMENT_REVIEW_TITLE =
  'Katteta ettemaks üle tähtaja — laekumata ja lõpparveta. Kontrolli, kas tehing on jõus.';
export const PREPAYMENT_REVIEW_CLS =
  'shrink-0 rounded bg-[hsl(var(--pending))]/20 px-1 text-[9px] font-semibold leading-none text-[hsl(var(--pending))]';

/**
 * Always-on italic on a forecast entry's AMOUNT — the same lightweight "not yet a
 * confirmed figure" cue in every per-entry lens (calendar chip amount + register Summa),
 * layered on top of the marker pill so the number itself also reads tentative. Empty for
 * an actual/committed entry. Pure display; never touches a number (L9); one definition,
 * both lenses (L14).
 */
export function forecastAmountClass(certainty: 'actual' | 'committed' | 'forecast'): string {
  return certainty === 'forecast' ? 'italic' : '';
}

/**
 * Does a finance-core certainty-bucket sum CONTAIN forecast money for the displayed
 * field? The shared predicate behind the matrix's per-cell tentative cue (the amber
 * amount + dashed-circle mark)
 * (an AGGREGATE cell can mix confirmed and forecast documents — the calendar chip and
 * the register row are per-entry and read `certainty` directly instead). A pure
 * zero-check over finance-core's `CertaintyBuckets.forecast` — it never computes an
 * amount (L9); one definition, so every aggregate cell decides "contains a plan"
 * identically (L14).
 */
export function bucketsHaveForecast(buckets: { forecast: MoneySum }, mode: MoneyMode): boolean {
  return pickSum(buckets.forecast, mode) !== 0;
}

/**
 * Estonian on-screen label per settlement status — the ONE place each status is
 * named (mirrors the approved canvas / terminology in replit.md). finance-core
 * OWNS the status (`settlementStatusOf`); the UI only labels and styles it, so
 * both lenses render an identical badge (L14 lens parity).
 */
export const SETTLEMENT_STATUS_LABEL: Record<SettlementStatus, string> = {
  settled: 'Tasutud',
  overdue: 'Üle tähtaja',
  partial: 'Osaliselt',
  planned: 'Plaan',
  pending: 'Ootel',
};

/**
 * Badge + dot classes per settlement status, DIRECTION-AWARE (inherited dark-dense
 * tokens; L8). A receivable that settled reads income-green (Laekunud); a payable that
 * settled reads the calmer settled-cyan (Makstud) — so the two never look identical (the
 * register readability fix). Overdue = cost-red, partial = pending-amber, an unpaid
 * `pending` is muted, and a `planned` (forecast) keeps its dashed primary outline.
 * finance-core OWNS the status (`settlementStatusOf`); this is the ONE place the status
 * COLOUR lives, so the register cell, the shared settlement detail badge AND the donut
 * `SettlementRing` (both lenses) read the SAME mapping (L14 lens parity). `ring` is the raw
 * `hsl(var(--x))` stroke value for the SVG donut, kept here so the badge and the ring can
 * never diverge. Mirrors `settlementStatusLabel`'s direction split.
 */
export function settlementStatusStyle(
  status: SettlementStatus,
  kind: 'income' | 'cost',
): { badge: string; dot: string; ring: string } {
  if (status === 'overdue') {
    return {
      badge: 'border border-[hsl(var(--cost))]/40 bg-[hsl(var(--cost))]/12 text-[hsl(var(--cost))]',
      dot: 'bg-[hsl(var(--cost))]',
      ring: 'hsl(var(--cost))',
    };
  }
  if (status === 'partial') {
    return {
      badge: 'border border-[hsl(var(--pending))]/40 bg-[hsl(var(--pending))]/12 text-[hsl(var(--pending))]',
      dot: 'bg-[hsl(var(--pending))]',
      ring: 'hsl(var(--pending))',
    };
  }
  if (status === 'settled') {
    return kind === 'income'
      ? {
          badge: 'border border-[hsl(var(--income))]/40 bg-[hsl(var(--income))]/12 text-[hsl(var(--income))]',
          dot: 'bg-[hsl(var(--income))]',
          ring: 'hsl(var(--income))',
        }
      : {
          badge: 'border border-[hsl(var(--settled))]/40 bg-[hsl(var(--settled))]/12 text-[hsl(var(--settled))]',
          dot: 'bg-[hsl(var(--settled))]',
          ring: 'hsl(var(--settled))',
        };
  }
  if (status === 'planned') {
    return {
      badge: 'border border-dashed border-[hsl(var(--primary))]/60 text-[hsl(var(--primary))]',
      dot: 'bg-[hsl(var(--primary))]',
      ring: 'hsl(var(--primary))',
    };
  }
  return {
    badge: 'border border-border bg-muted/30 text-muted-foreground',
    dot: 'bg-muted-foreground/60',
    ring: 'hsl(var(--muted-foreground))',
  };
}

/**
 * Direction-aware Estonian label for a settlement status — a receivable *laekub*
 * (Laekunud / Laekumata), a payable *makstakse* (Makstud / Maksmata), and an
 * overdue document reads "Üle tähtaja" for both directions. finance-core OWNS the
 * status (`settlementStatusOf`); this is the ONE place the direction-specific
 * WORDING lives, so the register badge and the matrix's carry-forward labels
 * never drift apart (L14 — one truth per term). `SETTLEMENT_STATUS_LABEL` stays
 * the directionless term used by the status facet/filter chips.
 *
 * `pending` and `planned` are both "unpaid" states, so they share the unpaid word
 * (Laekumata / Maksmata) — the certainty difference is shown by the badge style,
 * not the label.
 */
export function settlementStatusLabel(status: SettlementStatus, kind: 'income' | 'cost'): string {
  if (status === 'overdue') return SETTLEMENT_STATUS_LABEL.overdue;
  if (status === 'settled') return kind === 'income' ? 'Laekunud' : 'Makstud';
  if (status === 'partial') return kind === 'income' ? 'Osaliselt laekunud' : 'Osaliselt makstud';
  return kind === 'income' ? 'Laekumata' : 'Maksmata';
}

/**
 * Estonian on-screen label per priority flag (KRIITILINE/OLULINE/FOOKUSES) — the
 * ONE place each priority is named. Both lenses + the register render this label
 * so a prioritised entry reads identically everywhere (L14 lens parity).
 */
export const PRIORITY_LABEL: Record<Priority, string> = {
  critical: 'Kriitiline',
  important: 'Oluline',
  focus: 'Fookuses',
};

/** Badge classes per priority (inherited dark-dense tokens; L8). Critical = cost
 *  red urgency, important = pending amber, focus = primary accent. */
export const PRIORITY_CLS: Record<Priority, string> = {
  critical: 'bg-[hsl(var(--cost))]/15 text-[hsl(var(--cost))]',
  important: 'bg-[hsl(var(--pending))]/15 text-[hsl(var(--pending))]',
  focus: 'bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))]',
};

export interface CashFlowRequestOptions {
  /** Injectable fetch (tests). Defaults to the global fetch. */
  fetchImpl?: typeof fetch;
  /** Base URL; defaults to same-origin (relative) so it works everywhere. */
  baseUrl?: string;
  /** Supabase access token; attached as a Bearer header when present. */
  accessToken?: string;
}

export interface CashFlowRange {
  from?: string;
  to?: string;
}

function authHeaders(accessToken: string | undefined, base: Record<string, string> = {}): Record<string, string> {
  return accessToken ? { ...base, Authorization: `Bearer ${accessToken}` } : base;
}

/** Read the rolled-up cash-flow window (days + entries + balance anchor). */
export async function fetchCashFlow(
  range: CashFlowRange = {},
  options: CashFlowRequestOptions = {},
): Promise<CashFlowResponse> {
  const { fetchImpl = fetch, baseUrl = '', accessToken } = options;
  const params = new URLSearchParams();
  if (range.from) params.set('from', range.from);
  if (range.to) params.set('to', range.to);
  const query = params.toString();
  const url = `${baseUrl}/api/cash-flow${query ? `?${query}` : ''}`;

  const res = await fetchImpl(url, { headers: authHeaders(accessToken) });
  if (!res.ok) {
    throw new Error(`Cash-flow request failed: ${res.status}`);
  }
  return cashFlowResponseSchema.parse(await res.json());
}

/** Body to create a manual cash-flow entry; the API splits the amount into Money. */
export interface CreateCashFlowEntryInput {
  kind: 'income' | 'cost';
  party: string;
  description?: string;
  category?: CashFlowEntry['category'];
  certainty?: CashFlowEntry['certainty'];
  source?: CashFlowEntry['source'];
  expectedDate: string;
  amount: { cents: number; basis: 'net' | 'gross' };
  vatRate?: number;
  vatMode?: CashFlowEntry['money']['vatMode'];
  /** Document type (Arve / Ettemaks / …); omitted on a quick entry. */
  docType?: NonNullable<CashFlowEntry['docType']>;
  /** Transaction type (Jaemüük / Projektimüük / …); omitted on a quick entry. */
  txnType?: NonNullable<CashFlowEntry['txnType']>;
  /** Effective project code; omitted = none. */
  project?: string;
  /** Document author/owner; omitted = none. */
  author?: string;
  /** Free-text "Kommentaar"; omitted = none. */
  comment?: string;
  /** Importance flag set on create; omitted/null = none. */
  priority?: CashFlowEntry['priority'];
}

/**
 * Body to create a manual entry as a PAYMENT SCHEDULE (module 1.3, "Osadena"): the
 * same fields as a single create, but the amount is paid/received in >=2 instalments
 * instead of on one `expectedDate`. Each instalment is a due day + its GROSS cents;
 * the parts must sum to the entered amount's gross (the API re-validates + carves the
 * canonical Money per part via finance-core — the client never does money maths, L9).
 */
export interface CreateCashFlowScheduleInput extends Omit<CreateCashFlowEntryInput, 'expectedDate'> {
  schedule: { expectedDate: string; cents: number }[];
}

/**
 * The seed every lens hands the ONE shared create dialog (L14). Each lens fills the
 * slice that is natural to it — the calendar a `date`, the table a line's
 * `kind`/`category`, the register/edge-FAB just `{ date: today }` — and the shared
 * `CashFlowView` owns the dialog + the create mutation. No lens owns create state.
 */
export interface CreateEntrySeed {
  date?: string;
  kind?: 'income' | 'cost';
  category?: CashFlowEntry['category'];
}

/**
 * Fields a planner edit can change: reschedule, re-certain, comment, priority.
 * Marking paid is NOT here — it is a settlement EVENT (`markCashFlowEntryPaid`,
 * the shared MarkPaidPanel flow), never a direct `actualDate` write (the legacy
 * field made a row read settled with no traceable payment).
 */
export interface UpdateCashFlowEntryInput {
  expectedDate?: string;
  certainty?: CashFlowEntry['certainty'];
  /** Free-text "Kommentaar"; `null` clears it. Editable from the register (PATCH). */
  comment?: string | null;
  /** Importance flag; `null` clears it. Editable from the register (PATCH). */
  priority?: CashFlowEntry['priority'];
}

const errorEnvelopeSchema = z.object({ error: z.object({ message: z.string() }) });

/** Surface the API error envelope's message (WHY a mutation failed) when present. */
async function failureMessage(res: Response, fallback: string): Promise<string> {
  const body: unknown = await res.json().catch(() => null);
  const parsed = errorEnvelopeSchema.safeParse(body);
  return parsed.success ? parsed.data.error.message : fallback;
}

async function mutate(
  url: string,
  method: 'POST' | 'PATCH',
  body: unknown,
  options: CashFlowRequestOptions,
): Promise<CashFlowEntry> {
  const { fetchImpl = fetch, baseUrl = '', accessToken } = options;
  const res = await fetchImpl(`${baseUrl}${url}`, {
    method,
    headers: authHeaders(accessToken, { 'Content-Type': 'application/json' }),
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw new Error(await failureMessage(res, `Cash-flow ${method} failed: ${res.status}`));
  }
  return entryEnvelopeSchema.parse(await res.json()).entry;
}

export function createCashFlowEntry(
  input: CreateCashFlowEntryInput,
  options: CashFlowRequestOptions = {},
): Promise<CashFlowEntry> {
  return mutate('/api/cash-flow/entries', 'POST', input, options);
}

// ---------------------------------------------------------------------------
// Sheets export
// ---------------------------------------------------------------------------

const sheetsExportResponseSchema = z.object({ url: z.string(), spreadsheetId: z.string() });
export type SheetsExportResponse = z.infer<typeof sheetsExportResponseSchema>;

/** POST /api/cash-flow/export-sheets?year=YYYY → creates a Google Sheet and returns its URL. */
export async function exportCashFlowToSheets(
  year: number,
  options: CashFlowRequestOptions = {},
): Promise<SheetsExportResponse> {
  const { fetchImpl = fetch, baseUrl = '', accessToken } = options;
  const res = await fetchImpl(`${baseUrl}/api/cash-flow/export-sheets?year=${year}`, {
    method: 'POST',
    headers: authHeaders(accessToken),
  });
  if (!res.ok) {
    const msg = await failureMessage(
      res,
      res.status === 503
        ? 'Google Sheetsi eksport ei ole selles keskkonnas seadistatud.'
        : `Eksport ebaõnnestus (${res.status})`,
    );
    throw new Error(msg);
  }
  return sheetsExportResponseSchema.parse(await res.json());
}

/**
 * Create a manual entry paid in instalments (module 1.3, "Osadena"). The API inserts
 * all parts atomically (one shared group_id, no origin ghost) and returns every part,
 * so the caller can refresh the shared cache and BOTH lenses render the ÷ instalments.
 */
export async function createCashFlowEntrySchedule(
  input: CreateCashFlowScheduleInput,
  options: CashFlowRequestOptions = {},
): Promise<CashFlowEntry[]> {
  const { fetchImpl = fetch, baseUrl = '', accessToken } = options;
  const res = await fetchImpl(`${baseUrl}/api/cash-flow/entries/schedule`, {
    method: 'POST',
    headers: authHeaders(accessToken, { 'Content-Type': 'application/json' }),
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    throw new Error(`Cash-flow schedule failed: ${res.status}`);
  }
  return splitResponseSchema.parse(await res.json()).entries;
}

export function updateCashFlowEntry(
  id: string,
  patch: UpdateCashFlowEntryInput,
  options: CashFlowRequestOptions = {},
): Promise<CashFlowEntry> {
  return mutate(`/api/cash-flow/entries/${id}`, 'PATCH', patch, options);
}

/** The API's confirmation envelope for a hard delete — Zod-validated at the boundary. */
const deleteResponseSchema = z.object({ deleted: z.literal(true), id: z.string() });
export type DeleteCashFlowEntryResult = z.infer<typeof deleteResponseSchema>;

/**
 * Delete a manually-created cash-flow entry (module 1.3). The API hard-guards this
 * to `source='manual'` rows (a synced row → 409) and audits the delete server-side;
 * naturally idempotent, so a React-Query retry is safe.
 */
export async function deleteCashFlowEntry(
  id: string,
  options: CashFlowRequestOptions = {},
): Promise<DeleteCashFlowEntryResult> {
  const { fetchImpl = fetch, baseUrl = '', accessToken } = options;
  const res = await fetchImpl(`${baseUrl}/api/cash-flow/entries/${id}`, {
    method: 'DELETE',
    headers: authHeaders(accessToken),
  });
  if (!res.ok) {
    throw new Error(`Cash-flow DELETE failed: ${res.status}`);
  }
  return deleteResponseSchema.parse(await res.json());
}

/**
 * The mark-paid INTENT the shared MarkPaidPanel collects (module 1.3, L14 — the SAME
 * function from the register popover and the entry-detail Arveldus): the settlement
 * date + an optional PARTIAL amount in integer cents. No amount = mark the FULL
 * outstanding gap; the server validates any partial against the authoritative gap
 * (over-proposal → 400), so the panel never decides money (L9).
 */
export interface MarkPaidIntent {
  /** Settlement date (ISO). */
  paidOn: string;
  /** Partial amount (integer cents); omitted = the full outstanding gap. */
  amountCents?: number;
}

/**
 * "Märgi laekunuks/makstuks" body (module 1.3): record a Manuaal settlement EVENT
 * for the entry. `amountCents` is an optional PARTIAL proposal the API validates
 * against the authoritative outstanding gap it derives via finance-core — omitted,
 * the API books the full gap (L9). `method` is the DB-pinned vocabulary;
 * `idempotencyKey` is minted once per click by the orchestrator so a React-Query
 * retry re-applies the SAME settlement at-least-once.
 */
export interface MarkPaidInput {
  /** Settlement date (ISO); the API defaults to today when omitted. */
  paidOn?: string;
  /** Partial amount (integer cents); omitted = the API books the full gap. */
  amountCents?: number;
  /** Payment method; null/omitted = the human did not record one. */
  method?: 'TRANSFER' | 'CARD' | 'CASH' | null;
  idempotencyKey: string;
}

/** Parse a user-typed euro amount (comma or dot) to integer cents, or null when it is
 *  not a clean positive number or would overflow the safe cents range. UI input → cents
 *  only; never money math (L9). ONE parse + overflow guard shared by the register's
 *  split/resolve panels and the mark-paid panel. */
export function parseEurCents(raw: string): number | null {
  const r = raw.replace(',', '.').trim();
  if (!/^\d+(?:\.\d+)?$/.test(r)) return null;
  try {
    return toCents(Number.parseFloat(r));
  } catch {
    return null;
  }
}

/** Record a Manuaal settlement event for an entry; returns the settled entry. */
export function markCashFlowEntryPaid(
  id: string,
  input: MarkPaidInput,
  options: CashFlowRequestOptions = {},
): Promise<CashFlowEntry> {
  return mutate(`/api/cash-flow/entries/${id}/mark-paid`, 'POST', input, options);
}

/**
 * "Tühista käsitsi makse" — the UNDO of `markCashFlowEntryPaid`: retracts the
 * entry's Manuaal (source='manual') settlement events after a mis-click. ONLY the
 * human observation is removed — synced sources (LHV/SA/Erply) are other systems'
 * facts and stay untouched. Naturally idempotent (a retried DELETE removes 0 rows),
 * so no idempotency key. Returns the entry with its settlement state re-derived.
 */
export async function removeManualPayments(
  id: string,
  options: CashFlowRequestOptions = {},
): Promise<CashFlowEntry> {
  const { fetchImpl = fetch, baseUrl = '', accessToken } = options;
  const res = await fetchImpl(`${baseUrl}/api/cash-flow/entries/${id}/mark-paid`, {
    method: 'DELETE',
    headers: authHeaders(accessToken),
  });
  if (!res.ok) {
    throw new Error(`Cash-flow DELETE failed: ${res.status}`);
  }
  return entryEnvelopeSchema.parse(await res.json()).entry;
}

/** Body to split an entry: move part of its GROSS to a new due day (rest stays put). */
export interface SplitCashFlowEntryInput {
  targetDate: string;
  moveGrossCents: number;
}

/**
 * Resolve an entry's outstanding gap (module 1.3). A write-off (rounding / bad
 * debt) silently records the adjustment; factoring ALSO books the fee as a real
 * `financial_cost` line. The closed amount is the server-derived outstanding — the
 * client never sends it; it only states the resolution KIND + a reason (+ the fee
 * for factoring), which is the resolution INTENT the register collects in the
 * "Lahenda" panel WITHOUT the idempotency key. The key is minted once per click by
 * the orchestrator (`CashFlowView`) so a React-Query retry re-applies the SAME
 * action at-least-once safely; the register never invents money (L9).
 */
export type ResolveSettlementIntent =
  | {
      // `retention_deferred` closes a gap the customer lawfully withheld as
      // ehitus-/garantiitagatis — like the write-offs it books NO fee line.
      type: 'rounding_writeoff' | 'bad_debt_writeoff' | 'retention_deferred';
      reason: string;
      effectiveOn?: string;
    }
  | {
      type: 'factoring_fee';
      reason: string;
      effectiveOn?: string;
      fee: { cents: number; basis: 'net' | 'gross' };
      factor: string;
      vatRate?: number;
      vatMode?: 'domestic' | 'foreign' | 'reduced' | 'exempt';
    };

/** A resolution intent plus the orchestrator-minted idempotency key (the API body). */
export type ResolveSettlementInput = ResolveSettlementIntent & { idempotencyKey: string };

/**
 * Split one entry into a linked installment: the API carves `moveGrossCents` off the
 * source (via finance-core) onto `targetDate` and leaves the remainder as the origin
 * ghost. Returns BOTH affected rows (ghost + moved part) so the caller can refresh.
 */
export async function splitCashFlowEntry(
  id: string,
  input: SplitCashFlowEntryInput,
  options: CashFlowRequestOptions = {},
): Promise<CashFlowEntry[]> {
  const { fetchImpl = fetch, baseUrl = '', accessToken } = options;
  const res = await fetchImpl(`${baseUrl}/api/cash-flow/entries/${id}/split`, {
    method: 'POST',
    headers: authHeaders(accessToken, { 'Content-Type': 'application/json' }),
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    throw new Error(`Cash-flow split failed: ${res.status}`);
  }
  return splitResponseSchema.parse(await res.json()).entries;
}

/**
 * Resolve an entry's outstanding settlement gap. Returns BOTH affected rows when
 * factoring (the settled receivable + the new fee line) or just the resolved entry
 * for a write-off, so the caller can refresh the shared cash-flow cache.
 */
export async function resolveSettlement(
  id: string,
  input: ResolveSettlementInput,
  options: CashFlowRequestOptions = {},
): Promise<CashFlowEntry[]> {
  const { fetchImpl = fetch, baseUrl = '', accessToken } = options;
  const res = await fetchImpl(`${baseUrl}/api/cash-flow/entries/${id}/resolve`, {
    method: 'POST',
    headers: authHeaders(accessToken, { 'Content-Type': 'application/json' }),
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    throw new Error(`Cash-flow resolve failed: ${res.status}`);
  }
  return splitResponseSchema.parse(await res.json()).entries;
}

/**
 * "Seo ettemaksuga" body (Task 2): link a received prepayment (ETT) to this final
 * invoice (ARV) so the final stops reading "Üle tähtaja" and the same euro is not
 * double-counted. The client states only the LINK FACTS — which prepayment, the
 * capacity (`amountCents`), and an optional `effectiveOn` (the API defaults to today);
 * finance-core RESOLVES the euro offset (L9 — never computed here). `idempotencyKey`
 * is minted once per click by the orchestrator so a React-Query retry re-applies the
 * SAME link at-least-once safely (the DB's nullable-unique key collapses the retry).
 */
export interface LinkPrepaymentInput {
  /** The prepayment (ETT) entry id funding this final invoice. */
  prepaymentEntryId: string;
  /** The offset capacity, integer cents (L9) — the API gates it by the ETT's cash. */
  amountCents: number;
  /** When the offset takes effect (ISO); the API defaults to today when omitted. */
  effectiveOn?: string;
  idempotencyKey: string;
}

/**
 * Link a prepayment to a final invoice. Returns BOTH affected rows (the prepayment +
 * the final), re-projected, so the caller can refresh the shared cash-flow cache.
 */
export async function linkPrepayment(
  finalId: string,
  input: LinkPrepaymentInput,
  options: CashFlowRequestOptions = {},
): Promise<CashFlowEntry[]> {
  const { fetchImpl = fetch, baseUrl = '', accessToken } = options;
  const res = await fetchImpl(`${baseUrl}/api/cash-flow/entries/${finalId}/prepayment-links`, {
    method: 'POST',
    headers: authHeaders(accessToken, { 'Content-Type': 'application/json' }),
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    throw new Error(`Cash-flow prepayment link failed: ${res.status}`);
  }
  return splitResponseSchema.parse(await res.json()).entries;
}

/**
 * Remove a prepayment-offset link by its id. Returns BOTH affected rows, re-projected
 * (the final reverts to its full outstanding, the prepayment frees its capacity), so
 * the caller can refresh the shared cache. Naturally idempotent — a retry on a
 * gone link is safe.
 */
export async function unlinkPrepayment(
  linkId: string,
  options: CashFlowRequestOptions = {},
): Promise<CashFlowEntry[]> {
  const { fetchImpl = fetch, baseUrl = '', accessToken } = options;
  const res = await fetchImpl(`${baseUrl}/api/cash-flow/entry-links/${linkId}`, {
    method: 'DELETE',
    headers: authHeaders(accessToken),
  });
  if (!res.ok) {
    throw new Error(`Cash-flow prepayment unlink failed: ${res.status}`);
  }
  return splitResponseSchema.parse(await res.json()).entries;
}

/** A distinct project the searchable Projekt picker offers (module 1.3). */
export const projectSummarySchema = z.object({
  project: z.string(),
  projectName: z.string().nullable(),
  entryCount: z.number().int().min(0),
});
const projectsResponseSchema = z.object({ projects: z.array(projectSummarySchema) });
export type ProjectSummary = z.infer<typeof projectSummarySchema>;

/** Options for the project search — the base request options plus a result cap. */
export interface ProjectSearchOptions extends CashFlowRequestOptions {
  /** Max results (the API caps it; omitted = the API default). */
  limit?: number;
}

/**
 * Search the distinct EFFECTIVE projects the EnrichmentForm offers (pick/search an
 * existing project, never free text). A blank query returns the full capped list.
 */
export async function searchProjects(
  query: string,
  options: ProjectSearchOptions = {},
): Promise<ProjectSummary[]> {
  const { fetchImpl = fetch, baseUrl = '', accessToken, limit } = options;
  const params = new URLSearchParams();
  if (query) params.set('q', query);
  if (limit !== undefined) params.set('limit', String(limit));
  const qs = params.toString();
  const url = `${baseUrl}/api/cash-flow/projects${qs ? `?${qs}` : ''}`;

  const res = await fetchImpl(url, { headers: authHeaders(accessToken) });
  if (!res.ok) {
    throw new Error(`Project search failed: ${res.status}`);
  }
  return projectsResponseSchema.parse(await res.json()).projects;
}

/**
 * The origin "ghost": a linked installment that still sits on the original due day
 * (the leftover remainder after one or more parts were moved off it). Rendered faded
 * to show "money used to be due here". A standalone (unsplit) entry is never a ghost.
 */
export function isGhostEntry(entry: CashFlowEntry): boolean {
  return entry.groupId !== null && entry.originExpectedDate === entry.expectedDate;
}

// ----------------------------------------------------------------- display
const EUR = new Intl.NumberFormat('et-EE', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** Integer cents → Estonian euro string (display only — never feeds a calc). */
export function formatEur(cents: number): string {
  return EUR.format(cents / 100);
}

const EUR0 = new Intl.NumberFormat('et-EE', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});
/** Compact euro (no cents) — for the dense per-day running-balance pills. */
export function formatEurCompact(cents: number): string {
  return EUR0.format(Math.round(cents / 100));
}

const NUM0 = new Intl.NumberFormat('et-EE', { maximumFractionDigits: 0 });
/** Grouped number, no € symbol — for tight hover-card amount rows. */
export function formatNumCompact(cents: number): string {
  return NUM0.format(Math.round(cents / 100));
}

/** Select the field the toggle asks for from a summed amount. */
export function pickSum(sum: MoneySum, mode: MoneyMode): number {
  return mode === 'net' ? sum.net : sum.gross;
}

/** Select the field the toggle asks for from an entry's Money. */
export function pickMoney(money: CashFlowEntry['money'], mode: MoneyMode): number {
  return mode === 'net' ? money.net : money.gross;
}

/**
 * Settled share (basis points from finance-core `settlementRatioSummary`) → an
 * Estonian whole-percent string for the footer's "tasutud" cue. A display ratio,
 * never money (L9) — finance-core owns the cents and the bps; this only formats.
 */
export function formatSettledPct(ratioBps: number): string {
  return `${Math.round(ratioBps / 100)}%`;
}

const DAY_FMT = new Intl.DateTimeFormat('et-EE', { day: '2-digit', month: '2-digit', year: 'numeric' });
const SHORT_DAY_FMT = new Intl.DateTimeFormat('et-EE', { day: '2-digit', month: '2-digit' });
const MONTH_FMT = new Intl.DateTimeFormat('et-EE', { month: 'long', year: 'numeric' });

/** ISO day → midday UTC Date (avoids any TZ rollover when only formatting). */
function isoToDate(iso: string): Date {
  return new Date(`${iso}T12:00:00Z`);
}

/** Estonian column label for a treasury period, by granularity. */
export function periodLabel(period: { from: string; to: string }, gran: 'day' | 'week' | 'month'): string {
  if (gran === 'month') return MONTH_FMT.format(isoToDate(period.from));
  if (gran === 'week') {
    return `${SHORT_DAY_FMT.format(isoToDate(period.from))}–${SHORT_DAY_FMT.format(isoToDate(period.to))}`;
  }
  return DAY_FMT.format(isoToDate(period.from));
}

// ---------------------------------------------------- matrix column headers
const MONTH_ONLY_FMT = new Intl.DateTimeFormat('et-EE', { month: 'long' });
/** Estonian weekday names, indexed by JS `getUTCDay()` (0=Sun … 6=Sat). */
const WEEKDAY_ET = ['Pühapäev', 'Esmaspäev', 'Teisipäev', 'Kolmapäev', 'Neljapäev', 'Reede', 'Laupäev'] as const;
/** Roman quarter numerals (the Estonian convention, e.g. "II kvartal"). */
const ROMAN_QUARTER = ['I', 'II', 'III', 'IV'] as const;

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function monthNameCap(iso: string): string {
  return capitalize(MONTH_ONLY_FMT.format(isoToDate(iso)));
}

/** ISO-8601 week number (weeks start Monday; week 1 holds the first Thursday). */
export function isoWeek(iso: string): number {
  const d = new Date(`${iso}T00:00:00Z`);
  const dow = (d.getUTCDay() + 6) % 7; // Mon=0 … Sun=6
  d.setUTCDate(d.getUTCDate() - dow + 3); // the Thursday of this week
  const firstThursday = new Date(Date.UTC(d.getUTCFullYear(), 0, 4));
  const ftDow = (firstThursday.getUTCDay() + 6) % 7;
  firstThursday.setUTCDate(firstThursday.getUTCDate() - ftDow + 3);
  return 1 + Math.round((d.getTime() - firstThursday.getTime()) / (7 * 86_400_000));
}

/** The quarter number (1–4) the ISO day's month falls in. */
function quarterOf(iso: string): number {
  return Math.floor((Number(iso.slice(5, 7)) - 1) / 3) + 1;
}

/** The two-line column header (label + sub) for a treasury period column. */
export function periodColumn(period: { from: string; to: string }, gran: MoneyModeGranularity): {
  label: string;
  sub: string;
} {
  if (gran === 'quarter') {
    // The column nests its three months as the sub-label (e.g. "Aprill–Juuni"),
    // reading the finance-core nested period spans so the months that make up the
    // quarter are the SAME ones it sums (one truth per number); the year is the
    // spanning group above. The first/last month frame the range.
    const q = quarterOf(period.from);
    const months = quarterMonths(`${period.from.slice(0, 4)}-Q${q}`);
    const first = monthNameCap(months[0].from);
    const last = monthNameCap(months[months.length - 1].from);
    return { label: `${ROMAN_QUARTER[q - 1]} kvartal`, sub: `${first}–${last}` };
  }
  if (gran === 'month') return { label: monthNameCap(period.from), sub: period.from.slice(0, 4) };
  if (gran === 'week') {
    return { label: `N${isoWeek(period.from)}`, sub: SHORT_DAY_FMT.format(isoToDate(period.from)) };
  }
  const weekday = WEEKDAY_ET[isoToDate(period.from).getUTCDay()];
  return { label: weekday, sub: SHORT_DAY_FMT.format(isoToDate(period.from)) };
}

/** The spanning group header above the column headers (week·month / month / year). */
export function periodGroup(period: { from: string; to: string }, gran: MoneyModeGranularity): string {
  if (gran === 'quarter') return period.from.slice(0, 4);
  if (gran === 'month') return period.from.slice(0, 4);
  if (gran === 'week') return monthNameCap(period.from);
  return `Nädal ${isoWeek(period.from)} · ${monthNameCap(period.from)}`;
}

/** Column-level certainty: past = actual, future = forecast, spanning today = committed. */
export function periodCertainty(period: { from: string; to: string }, today: string): 'actual' | 'committed' | 'forecast' {
  if (period.to < today) return 'actual';
  if (period.from > today) return 'forecast';
  return 'committed';
}

/** Whether a period column contains today (the highlighted "now" column). */
export function periodIsNow(period: { from: string; to: string }, today: string): boolean {
  return period.from <= today && today <= period.to;
}

type MoneyModeGranularity = 'day' | 'week' | 'month' | 'quarter';

// --------------------------------------------------- period quick-jump ranges
const EST_MONTHS = [
  'Jaanuar',
  'Veebruar',
  'Märts',
  'Aprill',
  'Mai',
  'Juuni',
  'Juuli',
  'August',
  'September',
  'Oktoober',
  'November',
  'Detsember',
] as const;

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

/** Last calendar day of a month (UTC, DST-safe): day 0 of the next month. */
function lastDayOfMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/** The ISO `[from,to]` window for a single month (`month` is 1–12). */
export function monthRange(year: number, month: number): CashFlowRange {
  const mm = pad2(month);
  return { from: `${year}-${mm}-01`, to: `${year}-${mm}-${pad2(lastDayOfMonth(year, month))}` };
}

/** The ISO `[from,to]` window for a quarter (`quarter` is 1–4). */
export function quarterRange(year: number, quarter: number): CashFlowRange {
  const startMonth = (quarter - 1) * 3 + 1;
  const endMonth = startMonth + 2;
  return {
    from: `${year}-${pad2(startMonth)}-01`,
    to: `${year}-${pad2(endMonth)}-${pad2(lastDayOfMonth(year, endMonth))}`,
  };
}

/** The ISO `[from,to]` window for a whole year. */
export function yearRange(year: number): CashFlowRange {
  return { from: `${year}-01-01`, to: `${year}-12-31` };
}

/** Estonian label for a month quick-jump, e.g. "Juuni 2026". */
export function monthLabel(year: number, month: number): string {
  return `${EST_MONTHS[month - 1]} ${year}`;
}

/** Estonian label for a quarter quick-jump, e.g. "II kvartal 2026". */
export function quarterLabel(year: number, quarter: number): string {
  return `${ROMAN_QUARTER[quarter - 1]} kvartal ${year}`;
}

/**
 * The zero-padded month (`'01'`..`'12'`) a window spans, or `'all'` when the window
 * is wider than one calendar month (a quarter, a whole year) or unbounded. Used by
 * the register's period picker to reflect the SHARED workspace window (L14) without
 * any date branching living in the component.
 */
export function windowMonth(range: CashFlowRange | undefined): string {
  const from = range?.from;
  const to = range?.to;
  if (from && to && from.slice(0, 7) === to.slice(0, 7)) return from.slice(5, 7);
  return 'all';
}

// ------------------------------------------------ month grid (range picker)
/** One day cell of a month grid: its ISO date, the day-of-month number, and whether
 *  it belongs to the grid's own month (vs a spill day from a neighbouring month). */
export interface CalendarCell {
  iso: string;
  day: number;
  inMonth: boolean;
}

/** Monday-first short weekday initials (E T K N R L P), aligned with `calendarMonthGrid`. */
export const WEEKDAYS_SHORT = ['E', 'T', 'K', 'N', 'R', 'L', 'P'] as const;

/**
 * The Monday-first 6×7 day grid for a calendar month (`month` is 1–12): 42 cells that
 * always fill six weeks, the edges padded with the trailing/leading days of the
 * neighbouring months (`inMonth: false`). Pure date math (no money) so the visual
 * range picker carries no date logic of its own — one source per computation.
 */
export function calendarMonthGrid(year: number, month: number): CalendarCell[] {
  const first = new Date(Date.UTC(year, month - 1, 1));
  const lead = (first.getUTCDay() + 6) % 7; // Mon=0 … Sun=6 — days shown before the 1st
  const start = new Date(first);
  start.setUTCDate(1 - lead); // back up to the Monday on/just before the 1st
  const cells: CalendarCell[] = [];
  for (let i = 0; i < 42; i += 1) {
    const d = new Date(start);
    d.setUTCDate(start.getUTCDate() + i);
    cells.push({
      iso: d.toISOString().slice(0, 10),
      day: d.getUTCDate(),
      inMonth: d.getUTCMonth() === month - 1,
    });
  }
  return cells;
}

/** Step a `(year, month 1–12)` pair by `delta` months, rolling the year over correctly. */
export function shiftMonth(year: number, month: number, delta: number): { year: number; month: number } {
  const zeroBased = year * 12 + (month - 1) + delta;
  return { year: Math.floor(zeroBased / 12), month: (zeroBased % 12) + 1 };
}

/** Estonian short date for an ISO `YYYY-MM-DD`, e.g. "28.06.2026". A malformed input
 *  is returned unchanged (defensive — never throws on partial/odd data). */
export function formatEtDate(iso: string): string {
  const [y, m, d] = iso.split('-');
  return d && m && y ? `${d}.${m}.${y}` : iso;
}

/** Parse an Estonian short date "PP.KK.AAAA" (DD.MM.YYYY, 1–2 digit day/month
 *  accepted) → ISO "YYYY-MM-DD", or null when the text is malformed or not a real
 *  calendar date (overflow like 31.02 / month 13 → null; leap years respected).
 *  The single parser the app trusts; UI never hand-rolls date parsing. */
export function parseEtDate(input: string): string | null {
  const m = input.trim().match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (!m) return null;
  const day = Number(m[1]);
  const month = Number(m[2]);
  const year = Number(m[3]);
  if (month < 1 || month > 12) return null;
  // Day 0 of the next month = last day of THIS month (leap-year aware).
  const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
  if (day < 1 || day > daysInMonth) return null;
  return `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}`;
}

/** Estonian label for a custom date range, e.g. "01.06.2026–30.06.2026". */
export function rangeLabel(from: string, to: string): string {
  return `${formatEtDate(from)}–${formatEtDate(to)}`;
}
